import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";

export default function NewOpportunity() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const [causes, setCauses] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    cause: "",
    mode: "local",
    location: "",
    date: "",
    is_ongoing: false,
    hours: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    api.get("/causes").then(({ data }) => {
      setCauses(data);
      setForm((f) => (f.cause ? f : { ...f, cause: data[0]?.id || "" }));
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api
      .get(`/opportunities/${id}`)
      .then(({ data }) => {
        if (user && data.organizer_id !== user.id && user.role !== "admin") {
          setNotAllowed(true);
          return;
        }
        setForm({
          title: data.title || "",
          description: data.description || "",
          cause: data.cause || "",
          mode: data.mode || "local",
          location: data.location || "",
          date: data.is_ongoing ? "" : (data.date || ""),
          is_ongoing: !!data.is_ongoing,
          hours: String(data.hours ?? ""),
        });
      })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit, user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const hoursNum = Number(form.hours);
    if (!hoursNum || hoursNum <= 0) {
      setErr("Please enter how many hours this shift takes.");
      return;
    }
    if (!form.is_ongoing && !form.date) {
      setErr("Pick a date or mark this opportunity as ongoing.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        cause: form.cause,
        mode: form.mode,
        location: form.location,
        date: form.is_ongoing ? "" : form.date,
        is_ongoing: form.is_ongoing,
        hours: hoursNum,
      };
      if (isEdit) {
        const { data } = await api.patch(`/opportunities/${id}`, payload);
        nav(`/opportunities/${data.id}`);
      } else {
        const { data } = await api.post("/opportunities", payload);
        nav(`/opportunities/${data.id}`);
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-10 text-ink-soft" data-testid="opp-edit-loading">Loading…</div>
      </Layout>
    );
  }
  if (notAllowed) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-10" data-testid="opp-edit-forbidden">
          <h1 className="font-display font-bold text-2xl">Not your opportunity</h1>
          <p className="text-ink-soft mt-2">Only the organizer can edit this one.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="new-opportunity-page" className="max-w-2xl mx-auto px-5 md:px-8 py-12">
        <span className="overline text-[var(--cm-primary)]">{isEdit ? "Edit" : "Host"}</span>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
          {isEdit ? "Edit opportunity" : "Post an opportunity"}
        </h1>
        <p className="text-ink-soft mt-2">
          {isEdit
            ? "Signed-up volunteers will be notified if you change the date, location, or hours."
            : "Tell volunteers what you need, when, and for how long."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <Field label="Title" testid="new-opp-title-field">
            <input
              data-testid="new-opp-title-input"
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              placeholder="River cleanup Saturday"
            />
          </Field>
          <Field label="Description" testid="new-opp-desc-field">
            <textarea
              data-testid="new-opp-description-input"
              required
              rows={5}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              placeholder="What will volunteers do? Anything to bring?"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Cause" testid="new-opp-cause-field">
              <select
                data-testid="new-opp-cause-input"
                value={form.cause}
                onChange={(e) => update("cause", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              >
                {causes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Mode" testid="new-opp-mode-field">
              <select
                data-testid="new-opp-mode-input"
                value={form.mode}
                onChange={(e) => update("mode", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              >
                <option value="local">Local / in person</option>
                <option value="online">Online / remote</option>
              </select>
            </Field>
          </div>
          <Field label={form.mode === "online" ? "Online platform (optional)" : "Location"} testid="new-opp-location-field">
            <input
              data-testid="new-opp-location-input"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
              placeholder={form.mode === "online" ? "Zoom / Google Meet" : "Riverfront Park, NY"}
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Date" testid="new-opp-date-field">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    data-testid="new-opp-ongoing-checkbox"
                    type="checkbox"
                    checked={form.is_ongoing}
                    onChange={(e) => update("is_ongoing", e.target.checked)}
                    className="h-4 w-4 rounded border-ink-soft text-[var(--cm-primary)] focus:ring-[var(--cm-primary)]/40"
                  />
                  <span>This is an <span className="font-semibold">ongoing</span> opportunity (no set date)</span>
                </label>
                {!form.is_ongoing && (
                  <input
                    data-testid="new-opp-date-input"
                    type="date"
                    value={form.date}
                    onChange={(e) => update("date", e.target.value)}
                    className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
                  />
                )}
                {form.is_ongoing && (
                  <p data-testid="new-opp-ongoing-note" className="text-xs text-ink-soft italic bg-app rounded-lg px-3 py-2 border border-ink-soft">
                    Volunteers will see this as an ongoing opportunity they can join anytime.
                  </p>
                )}
              </div>
            </Field>
            <Field label="Hours per shift" testid="new-opp-hours-field">
              <input
                data-testid="new-opp-hours-input"
                type="number"
                min={0.1}
                step="any"
                max={40}
                required
                inputMode="decimal"
                value={form.hours}
                onChange={(e) => update("hours", e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3"
                placeholder="e.g. 2.5"
              />
              <p className="text-xs text-ink-soft mt-1.5">
                Enter any duration — decimals welcome (e.g. 1.75 for 1h 45min).
              </p>
            </Field>
          </div>
          {err && (
            <div data-testid="new-opp-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
              {err}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button data-testid="new-opp-submit-btn" type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Post opportunity"}
            </button>
            {isEdit && (
              <button
                data-testid="new-opp-cancel-btn"
                type="button"
                onClick={() => nav(`/opportunities/${id}`)}
                className="btn-outline text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
